'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '@/providers/auth-provider';
import { staffChatWebSocketURL, type ChatEvent } from '@/lib/api/chat';
import { playChatChimeAsync, unlockChatChime } from '@/lib/chat-chime';

export type ChatStreamConnection = 'connecting' | 'connected' | 'disconnected';

export const CHAT_ACTIVITY_EVENT = 'giglan-chat-activity';

type ChatStreamContextValue = {
  connection: ChatStreamConnection;
  reconnectExhausted: boolean;
  sendTyping: (conversationId: string, body: string, isTyping: boolean) => void;
};

const ChatStreamContext = createContext<ChatStreamContextValue>({
  connection: 'disconnected',
  reconnectExhausted: false,
  sendTyping: () => undefined,
});

export function useChatStream() {
  return useContext(ChatStreamContext);
}

const BASE_RECONNECT_MS = 1200;
const MAX_RECONNECT_MS = 30000;
const MAX_ATTEMPTS = 30;

function shouldNotifyUser(detail: ChatEvent, currentUserId: string | undefined): boolean {
  if (detail.type !== 'chat.message.created' || !detail.message) return false;
  const m = detail.message;
  if (m.author_kind === 'visitor' || m.author_kind === 'system') return true;
  if (m.author_kind === 'staff' && m.author_user_id && currentUserId && m.author_user_id !== currentUserId) {
    return true;
  }
  return false;
}

function requestBrowserNotifications() {
  if (typeof Notification === 'undefined' || Notification.permission !== 'default') return;
  try {
    void Notification.requestPermission();
  } catch {
    /* Browser policy can reject permission prompts outside a user gesture. */
  }
}

export function ChatStreamProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const [streamConnection, setStreamConnection] = useState<ChatStreamConnection>('disconnected');
  const [reconnectExhausted, setReconnectExhausted] = useState(false);
  const connection: ChatStreamConnection = isAuthenticated ? streamConnection : 'disconnected';
  const attemptRef = useRef(0);
  const userIdRef = useRef<string | undefined>(undefined);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    userIdRef.current = user?.user_id;
  }, [user?.user_id]);

  useEffect(() => {
    if (!isAuthenticated) return;

    requestBrowserNotifications();

    const onUserGesture = () => {
      void unlockChatChime();
      requestBrowserNotifications();
    };
    window.addEventListener('pointerdown', onUserGesture, { passive: true });
    window.addEventListener('keydown', onUserGesture);

    return () => {
      window.removeEventListener('pointerdown', onUserGesture);
      window.removeEventListener('keydown', onUserGesture);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let cancelled = false;

    const dispatchActivity = (detail: ChatEvent) => {
      window.dispatchEvent(new CustomEvent<ChatEvent>(CHAT_ACTIVITY_EVENT, { detail }));

      if (
        detail.type === 'chat.message.created' &&
        shouldNotifyUser(detail, userIdRef.current)
      ) {
        queueMicrotask(() => void playChatChimeAsync());
      }

      if (
        typeof document !== 'undefined' &&
        document.hidden &&
        typeof Notification !== 'undefined' &&
        Notification.permission === 'granted' &&
        shouldNotifyUser(detail, userIdRef.current)
      ) {
        const title = detail.conversation?.title || 'Новое сообщение';
        const body = (detail.message?.body || '').slice(0, 160);
        try {
          new Notification(title, { body, tag: detail.conversation_id || 'chat' });
        } catch {
          /* Safari / политики */
        }
      }
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      if (attemptRef.current >= MAX_ATTEMPTS) {
        setReconnectExhausted(true);
        setStreamConnection('disconnected');
        return;
      }
      const delay = Math.min(MAX_RECONNECT_MS, BASE_RECONNECT_MS * Math.pow(1.7, attemptRef.current));
      attemptRef.current += 1;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = undefined;
        connect();
      }, delay);
    };

    function connect() {
      if (cancelled) return;
      const url = staffChatWebSocketURL();
      if (!url) {
        setStreamConnection('disconnected');
        return;
      }
      socket?.close();
      socket = null;
      setStreamConnection('connecting');
      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch {
        setStreamConnection('disconnected');
        scheduleReconnect();
        return;
      }
      socket = ws;
      socketRef.current = ws;
      ws.onopen = () => {
        attemptRef.current = 0;
        setReconnectExhausted(false);
        setStreamConnection('connected');
      };
      ws.onmessage = (ev) => {
        try {
          const detail = JSON.parse(ev.data as string) as ChatEvent;
          dispatchActivity(detail);
        } catch {
          /* не JSON */
        }
      };
      ws.onerror = () => {
        /* close дальше сам перезайдёт */
      };
      ws.onclose = () => {
        if (cancelled) return;
        setStreamConnection('disconnected');
        if (socketRef.current === socket) socketRef.current = null;
        socket = null;
        scheduleReconnect();
      };
    }

    attemptRef.current = 0;
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      socket?.close();
      socketRef.current = null;
      setStreamConnection('disconnected');
    };
  }, [isAuthenticated]);

  const sendTyping = useCallback(
    (conversationId: string, body: string, isTyping: boolean) => {
      const ws = socketRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      try {
        ws.send(
          JSON.stringify({ type: 'typing', conversation_id: conversationId, body, is_typing: isTyping })
        );
      } catch {
        /* */
      }
    },
    []
  );

  const value = useMemo(
    () => ({ connection, reconnectExhausted, sendTyping }),
    [connection, reconnectExhausted, sendTyping]
  );
  return <ChatStreamContext.Provider value={value}>{children}</ChatStreamContext.Provider>;
}
